import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Paper, Typography, List, ListItem, 
  ListItemIcon, ListItemText, Button, 
  CircularProgress, Chip, Alert, Collapse 
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Add as AddIcon,
  Facebook as FacebookIcon,
  Google as GoogleIcon,
  Cached as SyncIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const IntegrationStatus = ({ clientId }) => {
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState({});
  const navigate = useNavigate();

  const fetchIntegrations = useCallback(async () => {
    try {
      const response = await axios.get(`/api/clients/${clientId}/integrations`, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Integration-Token': localStorage.getItem('integrationToken')
        }
      });
      setIntegrations(response.data.data.integrations);
      setError(null);
    } catch (err) {
      setError('Falha ao carregar integrações. Tente recarregar a página.');
      console.error('Erro ao buscar integrações:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    const checkUrlParams = async () => {
      const params = new URLSearchParams(window.location.search);
      const status = params.get('status');
      const message = params.get('message');

      if (status === 'error') {
        setError(decodeURIComponent(message) || 'Erro desconhecido durante a integração');
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    checkUrlParams();
    fetchIntegrations();
    
    const interval = setInterval(fetchIntegrations, 300000);
    return () => clearInterval(interval);
  }, [clientId, fetchIntegrations]);

  const handleSyncIntegration = async (integrationId, platform) => {
    try {
      setSyncing(prev => ({ ...prev, [integrationId]: true }));
      
      const endpoint = `/api/clients/${clientId}/${platform.replace('_', '-')}/${integrationId}/sync`;
      await axios.post(endpoint, {}, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Integration-Token': localStorage.getItem('integrationToken')
        }
      });

      await fetchIntegrations();
    } catch (err) {
      setError(`Erro na sincronização: ${err.response?.data?.message || err.message}`);
    } finally {
      setSyncing(prev => ({ ...prev, [integrationId]: false }));
    }
  };

  const PlatformIcon = ({ platform }) => {
    const icons = {
      meta: <FacebookIcon color="primary" />,
      google_analytics: <GoogleIcon color="success" />,
      google_ads: <GoogleIcon color="success" />,
    };
    return icons[platform] || <PendingIcon color="action" />;
  };

  const StatusIndicator = ({ status }) => {
    const statusConfig = {
      active: { icon: <CheckCircleIcon color="success" />, label: 'Conectado' },
      error: { icon: <ErrorIcon color="error" />, label: 'Erro' },
      expired: { icon: <ErrorIcon color="warning" />, label: 'Expirado' },
      revoked: { icon: <ErrorIcon color="error" />, label: 'Revogado' },
      default: { icon: <PendingIcon color="action" />, label: 'Pendente' }
    };
    
    const { icon, label } = statusConfig[status] || statusConfig.default;
    return <Box display="flex" alignItems="center" gap={1}>{icon} {label}</Box>;
  };

  return (
    <Paper 
      sx={{ 
        p: 3, 
        height: '100%', 
        position: 'relative',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid rgba(99, 102, 241, 0.1)',
        borderRadius: 3,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography 
          variant="h6" 
          component="h2"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Status das Integrações
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/clients/${clientId}/integrations/new`)}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
            },
          }}
        >
          Nova Integração
        </Button>
      </Box>

      <Collapse in={!!error}>
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      </Collapse>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : integrations.length === 0 ? (
        <Box textAlign="center" p={4}>
          <Typography variant="body1" color="text.secondary" mb={3} sx={{ fontWeight: 500 }}>
            Nenhuma integração configurada
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/clients/${clientId}/integrations/new`)}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
              },
            }}
          >
            Configurar Primeira Integração
          </Button>
        </Box>
      ) : (
        <List disablePadding>
          {integrations.map((integration) => (
            <ListItem
              key={integration._id}
              sx={{
                mb: 1.5,
                borderRadius: 2,
                background: 'rgba(99, 102, 241, 0.02)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: 'rgba(99, 102, 241, 0.05)',
                  borderColor: 'rgba(99, 102, 241, 0.2)',
                  transform: 'translateX(4px)',
                },
              }}
              secondaryAction={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={() => handleSyncIntegration(integration._id, integration.platform)}
                  disabled={!!syncing[integration._id]}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: 'rgba(99, 102, 241, 0.3)',
                    color: '#6366f1',
                    '&:hover': {
                      borderColor: '#6366f1',
                      background: 'rgba(99, 102, 241, 0.1)',
                    },
                  }}
                >
                  {syncing[integration._id] ? 'Sincronizando...' : 'Sincronizar'}
                </Button>
              }
            >
              <ListItemIcon>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlatformIcon platform={integration.platform} />
                </Box>
              </ListItemIcon>
              
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography 
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: 'text.primary' }}
                    >
                      {integration.accountName || integration.platform}
                    </Typography>
                    <Chip
                      label={integration.platform.replace('_', ' ')}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(99, 102, 241, 0.3)',
                        color: '#6366f1',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box mt={0.5}>
                    <StatusIndicator status={integration.status} />
                    {integration.lastSync && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                      >
                        Última sincronização: {formatDistanceToNow(new Date(integration.lastSync), {
                          addSuffix: true,
                          locale: ptBR
                        })}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
};

export default IntegrationStatus;
